import { useEffect } from "react";

interface DocumentMetaOptions {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
}

function getMeta(name: string): HTMLMetaElement | null {
  return document.querySelector(`meta[name="${name}"]`);
}

function getOgMeta(property: string): HTMLMetaElement | null {
  return document.querySelector(`meta[property="${property}"]`);
}

function setContent(el: Element | null, value: string | null | undefined): void {
  if (el && value != null) el.setAttribute("content", value);
}

export function useDocumentMeta(opts: DocumentMetaOptions | null): void {
  useEffect(() => {
    if (!opts) return;

    const prev = {
      title: document.title,
      description: getMeta("description")?.getAttribute("content") ?? "",
      canonical: document.querySelector<HTMLLinkElement>("link[rel='canonical']")?.getAttribute("href") ?? "",
      ogTitle: getOgMeta("og:title")?.getAttribute("content") ?? "",
      ogDescription: getOgMeta("og:description")?.getAttribute("content") ?? "",
      ogImage: getOgMeta("og:image")?.getAttribute("content") ?? "",
      ogType: getOgMeta("og:type")?.getAttribute("content") ?? "",
      twitterTitle: getMeta("twitter:title")?.getAttribute("content") ?? "",
      twitterDescription: getMeta("twitter:description")?.getAttribute("content") ?? "",
      twitterImage: getMeta("twitter:image")?.getAttribute("content") ?? "",
    };

    document.title = opts.title;
    setContent(getMeta("description"), opts.description);

    const canonicalEl = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (canonicalEl && opts.canonical) canonicalEl.setAttribute("href", opts.canonical);

    setContent(getOgMeta("og:title"), opts.ogTitle ?? opts.title);
    setContent(getOgMeta("og:description"), opts.ogDescription ?? opts.description);
    setContent(getOgMeta("og:type"), opts.ogType ?? "article");
    setContent(getMeta("twitter:title"), opts.ogTitle ?? opts.title);
    setContent(getMeta("twitter:description"), opts.ogDescription ?? opts.description);

    if (opts.ogImage) {
      setContent(getOgMeta("og:image"), opts.ogImage);
      setContent(getMeta("twitter:image"), opts.ogImage);
    }

    return () => {
      document.title = prev.title;
      setContent(getMeta("description"), prev.description);
      const c = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
      if (c) c.setAttribute("href", prev.canonical);
      setContent(getOgMeta("og:title"), prev.ogTitle);
      setContent(getOgMeta("og:description"), prev.ogDescription);
      if (prev.ogImage) setContent(getOgMeta("og:image"), prev.ogImage);
      setContent(getOgMeta("og:type"), prev.ogType);
      setContent(getMeta("twitter:title"), prev.twitterTitle);
      setContent(getMeta("twitter:description"), prev.twitterDescription);
      if (prev.twitterImage) setContent(getMeta("twitter:image"), prev.twitterImage);
    };
  }, [opts]);
}
